import {
  Geometry,
  GlProgram,
  Mesh,
  Shader,
  Texture,
  Container,
  UniformGroup,
} from "pixi.js";
import type { SpectralLineData } from "../types";
import { wavelengthToColor } from "../elements/wavelengthColor";
import { Spring } from "../interaction/Spring";

const BASE_THICKNESS_FRAC = 0.004;
const MESH_ROWS = 41;

// Easing time-constants for the held-state envelope (alpha attack/release).
const EXCITE_ATTACK_TAU = 0.04;
const EXCITE_RELEASE_TAU = 0.6;

// Width (in normalised vertical units) of the focused-highlight band along
// the line. Smaller = tighter spotlight at finger Y. The band fades to zero
// outside this radius using a cos² bell. Wider → the lit region extends
// further up and down the line so you can feel finger movement at the edges.
const HIGHLIGHT_BAND_WIDTH = 0.45;

const JITTER_FRAC = 0.05;

// Bow displacement envelope width — controls how far up/down the vibration
// extends from centre. Increased so the post-release ring visibly reaches
// closer to the top and bottom pin points.
export const BowParams = {
  width: 0.95,
};
export const ReleaseParams = {
  vibrationGain: 60,
};
// User-tunable highlight strength. Final per-line vertex alpha is roughly:
//   restAlpha * (1 + excite * (highlightGain - 1) * bandShape)
// so highlightGain = 1 → no boost, large values → very bright held bands.
export const HighlightParams = {
  intensity: 10,
};

// floor  – minimum thickness at both endpoints (0 = pinches to nothing).
// exponent – sin curve power; <1 broadens the peak, >1 narrows it.
// Default (floor=0.2, exponent=0.8) gives ≈ 0.20 / 0.81 / 1.00 / 0.81 / 0.20
// at t = 0 / 0.25 / 0.5 / 0.75 / 1.
export const ThicknessEnvelopeParams = {
  floor: 0.2,
  exponent: 0.8,
};

// When a finger is holding/playing a line, thicken the drawn stroke so it reads
// as a fat band under the fingertip instead of a hairline. Purely visual: the
// (u,v) sent upstream is still a single point. 1 = no change; the width eases to
// this multiple at full excite and back to 1 on release.
export const HeldThicknessParams = {
  boost: 2.6,
};

function thicknessEnvelope(t: number): number {
  if (t <= 0 || t >= 1) return ThicknessEnvelopeParams.floor;
  const { floor, exponent } = ThicknessEnvelopeParams;
  return floor + (1 - floor) * Math.pow(Math.sin(Math.PI * t), exponent);
}

function bowEnvelope(t: number): number {
  const w = BowParams.width;
  const arg = (t - 0.5) / w;
  if (arg <= -0.5 || arg >= 0.5) return 0;
  const c = Math.cos(Math.PI * arg);
  return c * c;
}

// Gaussian band centred at tCenter. HIGHLIGHT_BAND_WIDTH acts as σ — the
// value is 1 at the centre and falls off naturally without a hard cutoff.
// At d = 1 (one band-width away) the value is e^-4 ≈ 0.018, effectively 0.
function highlightBand(t: number, tCenter: number): number {
  const d = (t - tCenter) / HIGHLIGHT_BAND_WIDTH;
  return Math.exp(-4.0 * d * d);
}

const VERT = `#version 300 es
in vec2 aPosition;
in vec2 aUV;
in vec4 aColor;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vUv;
out vec4 vColor;

void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUv = aUV;
  vColor = aColor;
}
`;

// vColor.a carries the per-row brightness multiplier (1..many). Both the
// RGB and alpha are scaled by this value; clamping RGB to [0,1] keeps the
// hue while saturating toward the wavelength colour rather than white.
// With additive blending the net scene contribution is rgb*a, so the
// highlighted band glows in the line's own colour.
const FRAG = `#version 300 es
precision highp float;
in vec2 vUv;
in vec4 vColor;
out vec4 fragColor;

uniform sampler2D uTexture;
uniform vec4 uTint;

void main() {
  vec4 tex  = texture(uTexture, vUv);
  vec4 base = tex * uTint;
  // Scale the line's own colour by the highlight multiplier so the hue is
  // preserved. Clamping to 1 saturates toward the wavelength colour, not
  // toward white. With additive blending the scene contribution is rgb*a,
  // so both channels are boosted proportionally.
  vec3 rgb = min(base.rgb * vColor.a, vec3(1.0));
  float a = base.a * vColor.a;
  fragColor = vec4(rgb, a);
}
`;

export class Line {
  readonly data: SpectralLineData;
  readonly index: number;
  readonly color: number;
  restX: number;
  stageHeight: number;
  readonly restAlpha: number;
  baseThicknessPx: number;
  thicknessMultiplier: number = 1;
  hitBandPx: number;
  private maxDisplacementPx: number = 0;

  private mesh: Mesh<Geometry>;
  private positions: Float32Array;
  private colors: Float32Array;
  private tintUniformValue: Float32Array;
  spring: Spring;
  private holders = new Set<number>();
  private lastEffectiveX = NaN;
  private lastThicknessMul = NaN;
  private lastBowWidth = NaN;
  private lastEnvFloor = NaN;
  private lastEnvExponent = NaN;
  private excite = 0;
  // Normalised Y (0..1) where the highlight band is centred. Tracks finger
  // while held; targets 0.5 (line centre) on release.
  private bandCenterT = 0.5;
  private bandTargetT = 0.5;
  private lastFingerXOffset = 0;
  private lastBandCenterT = NaN;
  private lastExcite = NaN;
  private lastGeomExcite = NaN;
  private lastHighlightIntensity = NaN;

  constructor(
    data: SpectralLineData,
    index: number,
    falloffTex: Texture,
    stageWidth: number,
    stageHeight: number,
    parent: Container,
  ) {
    this.data = data;
    this.index = index;
    this.color = wavelengthToColor(data.wavelengthNm);
    this.restX = ((data.wavelengthNm - 400) / 300) * stageWidth;
    this.stageHeight = stageHeight;

    this.baseThicknessPx =
      BASE_THICKNESS_FRAC * stageWidth * data.thicknessMultiplier;
    this.hitBandPx = Math.max(1.1 * (this.baseThicknessPx / 2), 25);

    const positions = new Float32Array(MESH_ROWS * 2 * 2);
    const uvs = new Float32Array(MESH_ROWS * 2 * 2);
    const colors = new Float32Array(MESH_ROWS * 2 * 4); // RGBA per vertex
    for (let r = 0; r < MESH_ROWS; r++) {
      const t = r / (MESH_ROWS - 1);
      const y = t * stageHeight;
      const pi = r * 4;
      positions[pi + 0] = 0;
      positions[pi + 1] = y;
      positions[pi + 2] = 0;
      positions[pi + 3] = y;
      uvs[pi + 0] = 0;
      uvs[pi + 1] = t;
      uvs[pi + 2] = 1;
      uvs[pi + 3] = t;
      const ci = r * 8;
      // initialise to white with full alpha; updateVertexColors fills it.
      for (let k = 0; k < 8; k++) colors[ci + k] = 1;
    }

    const indices: number[] = [];
    for (let r = 0; r < MESH_ROWS - 1; r++) {
      const a = r * 2;
      const b = a + 1;
      const c = a + 2;
      const d = a + 3;
      indices.push(a, b, c, b, d, c);
    }

    const geometry = new Geometry({
      attributes: {
        aPosition: { buffer: positions, format: "float32x2" },
        aUV: { buffer: uvs, format: "float32x2" },
        aColor: { buffer: colors, format: "float32x4" },
      },
      indexBuffer: new Uint32Array(indices),
    });

    // Tint = wavelength color × restAlpha. Per-vertex aColor.a then modulates
    // the row's brightness on top (the focused-highlight band).
    const r = ((this.color >> 16) & 0xff) / 255;
    const g = ((this.color >> 8) & 0xff) / 255;
    const b = (this.color & 0xff) / 255;
    const intensity = Math.max(0, Math.min(1, data.intensity));
    this.restAlpha = Math.pow(intensity, 1 / 3);
    const tintValue = new Float32Array([r, g, b, this.restAlpha]);

    const uniforms = new UniformGroup({
      uTint: { value: tintValue, type: "vec4<f32>" },
    });
    const glProgram = GlProgram.from({ vertex: VERT, fragment: FRAG });
    const shader = new Shader({
      glProgram,
      resources: {
        lineUniforms: uniforms,
        uTexture: falloffTex.source,
      },
    });
    (shader as unknown as { texture: Texture }).texture = falloffTex;

    const mesh = new Mesh<Geometry>({
      geometry,
      shader: shader as unknown as Mesh["shader"],
    });
    mesh.blendMode = "normal";
    mesh.x = this.restX;
    mesh.y = 0;
    parent.addChild(mesh);

    this.mesh = mesh;
    this.positions = positions;
    this.colors = colors;
    this.tintUniformValue = tintValue;

    this.maxDisplacementPx = Math.min(
      stageWidth * 0.04,
      Math.max(stageHeight * 0.015, 20),
    );
    this.spring = new Spring(this.maxDisplacementPx);

    this.updateVertexColors(true);
    this.updateGeometry(true, 0);
  }

  setThicknessMultiplier(mul: number) {
    this.thicknessMultiplier = mul;
    this.spring.setMaxDisplacement(this.maxDisplacementPx);
  }

  resize(stageWidth: number, stageHeight: number) {
    this.stageHeight = stageHeight;

    this.restX = ((this.data.wavelengthNm - 400) / 300) * stageWidth;
    this.mesh.x = this.restX;

    this.baseThicknessPx =
      BASE_THICKNESS_FRAC * stageWidth * this.data.thicknessMultiplier;
    this.hitBandPx = Math.max(1.1 * (this.baseThicknessPx / 2), 25);

    this.maxDisplacementPx = Math.min(
      stageWidth * 0.04,
      Math.max(stageHeight * 0.015, 20),
    );
    this.spring.setMaxDisplacement(this.maxDisplacementPx);

    // Update vertex Y positions (X is handled by updateGeometry via mesh.x).
    const positions = this.positions;
    for (let r = 0; r < MESH_ROWS; r++) {
      const y = (r / (MESH_ROWS - 1)) * stageHeight;
      const pi = r * 4;
      positions[pi + 1] = y;
      positions[pi + 3] = y;
    }
    // Invalidate geometry dirty-check so the new positions are uploaded.
    this.lastEffectiveX = NaN;
    this.updateGeometry(true, 0);
  }

  contains(pointerX: number): boolean {
    return Math.abs(pointerX - this.restX) < this.hitBandPx;
  }

  distanceTo(pointerX: number): number {
    return Math.abs(pointerX - this.restX);
  }

  get isHeld(): boolean {
    return this.holders.size > 0;
  }

  addHolder(fingerIdx: number, fingerX: number, fingerY: number) {
    const isFirst = this.holders.size === 0;
    this.holders.add(fingerIdx);
    this.lastFingerXOffset = fingerX - this.restX;
    this.spring.release();
    this.spring.x = 0;
    this.spring.vx = 0;
    const tCenter = this.fingerYToBandT(fingerY);
    if (isFirst) this.bandCenterT = tCenter;
    this.bandTargetT = tCenter;
  }

  updateHolder(fingerIdx: number, fingerX: number, fingerY: number) {
    if (!this.holders.has(fingerIdx)) return;
    this.lastFingerXOffset = fingerX - this.restX;
    this.bandTargetT = this.fingerYToBandT(fingerY);
  }

  removeHolder(fingerIdx: number) {
    this.holders.delete(fingerIdx);
    if (this.holders.size === 0) {
      const sign = this.lastFingerXOffset < 0 ? -1 : 1;
      const mag =
        Math.abs(this.lastFingerXOffset) * ReleaseParams.vibrationGain;
      this.spring.release();
      this.spring.kick(sign * mag);
      // Released: band glides toward the line's vertical centre.
      this.bandTargetT = 0.5;
    }
  }

  private fingerYToBandT(fingerY: number): number {
    return Math.max(0.05, Math.min(0.95, fingerY / this.stageHeight));
  }

  step(dtSec: number) {
    this.spring.step(dtSec);

    const held = this.holders.size > 0;
    const swing = Math.min(
      1,
      Math.abs(this.spring.x) / Math.max(1, this.maxDisplacementPx),
    );
    const targetExcite = held ? 1 : swing;
    const tau =
      targetExcite > this.excite ? EXCITE_ATTACK_TAU : EXCITE_RELEASE_TAU;
    const mix = 1 - Math.exp(-dtSec / tau);
    this.excite += (targetExcite - this.excite) * mix;
    if (this.excite < 0.001) this.excite = 0;

    // Band centre eases between finger Y (held) and line centre (released).
    const bandTau = held ? EXCITE_ATTACK_TAU : EXCITE_RELEASE_TAU * 0.7;
    const bandMix = 1 - Math.exp(-dtSec / bandTau);
    this.bandCenterT += (this.bandTargetT - this.bandCenterT) * bandMix;

    this.updateVertexColors(false);

    let jitter = 0;
    if (!held && (this.spring.x !== 0 || this.spring.vx !== 0)) {
      jitter = (Math.random() * 2 - 1) * JITTER_FRAC * this.maxDisplacementPx;
    }
    this.updateGeometry(false, jitter);
  }

  private updateVertexColors(force: boolean) {
    const bc = this.bandCenterT;
    const ex = this.excite;
    const hi = HighlightParams.intensity;
    if (
      !force &&
      bc === this.lastBandCenterT &&
      ex === this.lastExcite &&
      hi === this.lastHighlightIntensity
    )
      return;
    this.lastBandCenterT = bc;
    this.lastExcite = ex;
    this.lastHighlightIntensity = hi;

    const colors = this.colors;
    // The line's overall row alpha is:
    //   baseLine    = 1                          (texture's vertical envelope already shapes the idle line)
    //   highlightUp = (hi - 1) * excite * bandShape(t)
    //   alpha_row   = baseLine + highlightUp
    // Multiplied per-pixel against the texture × tint, so dim lines stay
    // proportionally dim and bright lines push past the bloom threshold.
    for (let r = 0; r < MESH_ROWS; r++) {
      const t = r / (MESH_ROWS - 1);
      const band = highlightBand(t, bc);
      const aRow = 1 + (hi - 1) * ex * band;
      const ci = r * 8;
      // Two vertices per row, same alpha.
      colors[ci + 3] = aRow;
      colors[ci + 7] = aRow;
    }
    const buf = this.mesh.geometry.getBuffer("aColor");
    buf.update();
  }

  private updateGeometry(force: boolean, jitter: number) {
    const sx = this.spring.x + jitter;
    const tm = this.thicknessMultiplier;
    const bw = BowParams.width;
    const ef = ThicknessEnvelopeParams.floor;
    const ee = ThicknessEnvelopeParams.exponent;
    // Ease the drawn width toward the held boost with the same excite envelope
    // that drives the highlight, so a still finger still fattens the stroke.
    const ge = this.excite;
    if (
      !force &&
      sx === this.lastEffectiveX &&
      tm === this.lastThicknessMul &&
      bw === this.lastBowWidth &&
      ef === this.lastEnvFloor &&
      ee === this.lastEnvExponent &&
      ge === this.lastGeomExcite
    )
      return;
    this.lastEffectiveX = sx;
    this.lastThicknessMul = tm;
    this.lastBowWidth = bw;
    this.lastEnvFloor = ef;
    this.lastEnvExponent = ee;
    this.lastGeomExcite = ge;
    const heldMul = 1 + ge * (HeldThicknessParams.boost - 1);
    const halfWBase = (this.baseThicknessPx * tm * heldMul) / 2;
    const positions = this.positions;
    for (let r = 0; r < MESH_ROWS; r++) {
      const t = r / (MESH_ROWS - 1);
      const bow = bowEnvelope(t);
      const dx = bow * sx;
      const halfW = halfWBase * thicknessEnvelope(t);
      const i = r * 4;
      positions[i + 0] = -halfW + dx;
      positions[i + 2] = halfW + dx;
    }
    const buf = this.mesh.geometry.getBuffer("aPosition");
    buf.update();
  }

  destroy(parent: Container) {
    parent.removeChild(this.mesh);
    this.mesh.destroy();
    this.holders.clear();
    void this.tintUniformValue; // retained reference; explicit no-op so linter is content
  }
}
